import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo, JOURNEY_STEPS } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  Target, ShieldCheck, GitCompare, Trophy, Rocket,
  ArrowRight, ChevronRight, Menu, X, CheckCircle2,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1655462502318-a5ff79542ce7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxpbmRvbmVzaWFuJTIwc21hbGwlMjBidXNpbmVzcyUyMG93bmVyJTIwd29ya2luZ3xlbnwwfHx8fDE3ODkwNTA5NTN8MA&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  { icon: Target, title: "Rekomendasi Personal", desc: "Ide disesuaikan dengan modal, skill, waktu, aset, tujuan, dan batasan Anda." },
  { icon: ShieldCheck, title: "Modal Lebih Terjaga", desc: "Bukan sekadar mencari peluang, tetapi membantu menghindari bisnis yang terlalu berat untuk kondisi Anda." },
  { icon: GitCompare, title: "Validasi Terstruktur", desc: "Bandingkan kandidat berdasarkan personal fit, potensi bisnis, risiko, dan kemudahan eksekusi." },
  { icon: Trophy, title: "Business Winner", desc: "Dapatkan satu pilihan yang paling layak untuk diuji terlebih dahulu beserta alasan dan risikonya." },
  { icon: Rocket, title: "Rencana Eksekusi", desc: "Ubah pilihan bisnis menjadi langkah praktis untuk mulai menguji pasar." },
];

const HOW_STEPS = [
  "Ceritakan Kondisi Anda",
  "Dapatkan Ide yang Sesuai",
  "Pilih 2 Kandidat",
  "Validasi",
  "Temukan Business Winner",
  "Buat Rencana Eksekusi",
];

function TopNav() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const dest = user ? "/dashboard" : "/register";
  return (
    <header className="sticky top-0 z-50 border-b border-borderc bg-white/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <BrandLogo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-ink-soft">
            <a href="#masalah" className="hover:text-ink transition-colors">Masalah</a>
            <a href="#cara-kerja" className="hover:text-ink transition-colors">Cara Kerja</a>
            <a href="#manfaat" className="hover:text-ink transition-colors">Manfaat</a>
            <a href="#alur" className="hover:text-ink transition-colors">Alur Produk</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard"><Button data-testid="nav-dashboard-cta" className="bg-brand hover:bg-brand-hover">Dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium text-ink hover:text-brand transition-colors">Masuk</Link>
                <Link to="/register"><Button data-testid="nav-register-cta" className="bg-brand hover:bg-brand-hover">Daftar Gratis</Button></Link>
              </>
            )}
          </div>
          <button className="md:hidden rounded-lg p-2 hover:bg-slate-100" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="landing-mobile-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-borderc py-4 space-y-3">
            <a href="#masalah" onClick={() => setOpen(false)} className="block text-sm font-medium text-ink-soft">Masalah</a>
            <a href="#cara-kerja" onClick={() => setOpen(false)} className="block text-sm font-medium text-ink-soft">Cara Kerja</a>
            <a href="#manfaat" onClick={() => setOpen(false)} className="block text-sm font-medium text-ink-soft">Manfaat</a>
            <a href="#alur" onClick={() => setOpen(false)} className="block text-sm font-medium text-ink-soft">Alur Produk</a>
            <div className="flex flex-col gap-2 pt-2">
              <Link to="/login"><Button variant="outline" className="w-full">Masuk</Button></Link>
              <Link to={dest}><Button className="w-full bg-brand hover:bg-brand-hover">Daftar Gratis</Button></Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const start = () => navigate(user ? "/dashboard" : "/register");

  useEffect(() => { document.title = "IDEbisnis AI — Lebih yakin memilih ide bisnis yang cocok"; }, []);

  return (
    <div className="bg-surface">
      <TopNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-xs font-semibold text-brand" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" /> Asisten Keputusan Bisnis
              </span>
              <h1 className="mt-5 font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-ink" data-testid="hero-headline">
                Bingung Mau Mulai Bisnis Apa?
              </h1>
              <p className="mt-5 text-base sm:text-lg text-ink-soft leading-relaxed max-w-xl" data-testid="hero-subheadline">
                Temukan ide bisnis yang lebih cocok dengan modal, kemampuan, waktu, dan kondisi Anda — lalu validasi sebelum mengeluarkan modal lebih besar.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={start} data-testid="hero-primary-cta" className="bg-brand hover:bg-brand-hover text-base h-12 px-6">
                  Mulai Cari Ide Bisnis <ArrowRight className="ml-1 h-5 w-5" />
                </Button>
                <a href="#cara-kerja">
                  <Button size="lg" variant="outline" data-testid="hero-secondary-cta" className="w-full sm:w-auto text-base h-12 px-6 border-borderc">
                    Lihat Cara Kerjanya
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-ink-muted max-w-md">
                IDEbisnis AI membantu Anda menemukan, membandingkan, dan memvalidasi peluang bisnis berdasarkan kondisi Anda.
              </p>
            </div>

            <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="absolute -inset-3 rounded-3xl bg-brand/5" />
              <img
                src={HERO_IMG}
                alt="Diskusi strategi bisnis"
                className="relative rounded-2xl border border-borderc object-cover w-full h-[320px] sm:h-[420px] shadow-xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-5 left-4 right-4 sm:left-6 sm:right-auto sm:w-72 rounded-xl border border-borderc bg-white p-4 shadow-lg">
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-accent">Keputusan, bukan tebakan</div>
                <div className="mt-1 text-sm text-ink-soft">Mulai dari kondisi Anda → sampai bisnis yang paling layak diuji.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="masalah" className="border-y border-borderc bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink" data-testid="problem-headline">
              Masalahnya Bukan Kekurangan Ide.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-soft leading-relaxed">
              Internet penuh dengan ide bisnis. Masalah sebenarnya adalah mengetahui mana yang benar-benar pas untuk Anda.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              "Mana yang cocok dengan modal Anda",
              "Mana yang sesuai kemampuan Anda",
              "Mana yang realistis dengan waktu Anda",
              "Mana yang layak diuji",
              "Mana yang sebaiknya tidak dijalankan",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-borderc bg-surface p-5" data-testid={`problem-point-${i}`}>
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-accent" />
                <span className="text-sm sm:text-base text-ink">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-2xl border-l-4 border-brand pl-4 text-base sm:text-lg font-medium text-ink">
            IDEbisnis AI membantu mempersempit pilihan sebelum Anda mempertaruhkan modal.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="cara-kerja" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink" data-testid="how-headline">
            Cara Kerjanya
          </h2>
          <p className="mt-4 text-base sm:text-lg text-ink-soft">Enam langkah sederhana dari kondisi Anda menuju keputusan yang jelas.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {HOW_STEPS.map((step, i) => (
            <div key={i} className="rounded-xl border border-borderc bg-white p-6 transition-shadow hover:shadow-md" data-testid={`how-step-${i}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand text-white font-heading font-bold">{i + 1}</div>
              <div className="mt-4 font-heading text-lg font-semibold text-ink">{step}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section id="manfaat" className="border-y border-borderc bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink" data-testid="benefits-headline">
              Kenapa IDEbisnis AI
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-soft">Fokus pada keputusan yang lebih baik, bukan sekadar banyak ide.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="group rounded-2xl border border-borderc bg-surface p-6 sm:p-7 transition-all hover:border-brand-accent hover:shadow-md" data-testid={`benefit-card-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-heading text-lg sm:text-xl font-semibold text-ink">{title}</h3>
                <p className="mt-2 text-sm sm:text-base text-ink-soft leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT FLOW */}
      <section id="alur" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ink" data-testid="flow-headline">
            Alur Produk
          </h2>
          <p className="mt-4 text-base sm:text-lg text-ink-soft">Bukan semakin banyak ide. Semakin jelas keputusan.</p>
        </div>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-2" data-testid="product-flow">
          {JOURNEY_STEPS.map((s, i) => (
            <div key={s.key} className="flex lg:flex-col lg:flex-1 items-center gap-3 lg:gap-0">
              <div className="flex w-full flex-1 flex-col rounded-xl border border-borderc bg-white p-4 text-center">
                <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-brand/10 text-xs font-bold text-brand">{i + 1}</span>
                <span className="mt-2 font-mono text-xs font-semibold tracking-wide text-ink">{s.short}</span>
              </div>
              {i < JOURNEY_STEPS.length - 1 && (
                <ChevronRight className="h-5 w-5 shrink-0 rotate-90 text-brand-accent lg:rotate-0" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white" data-testid="cta-headline">
            Jangan Mulai dari Tebak-Tebakan.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed">
            Mulai dari kondisi Anda, bandingkan pilihan, lalu uji bisnis yang paling masuk akal.
          </p>
          <Button size="lg" onClick={start} data-testid="cta-primary" className="mt-8 h-12 bg-white px-8 text-base font-semibold text-brand hover:bg-white/90">
            Mulai Analisis Saya <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-borderc bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandLogo />
          <p className="text-sm text-ink-muted">© {new Date().getFullYear()} IDEbisnis AI. Lebih yakin memilih ide bisnis yang cocok dengan kondisi Anda.</p>
        </div>
      </footer>
    </div>
  );
}
